import { useState, useEffect, Fragment } from 'react'
import { convertPdfToExcel, validatePdfFile } from '../utils/fileUtils';
import { getUserData } from '../utils/auth';
import { getUserCredits } from '../utils/api';
import { startUseCredit } from '../utils/creditSystem';
import JSZip from 'jszip';
import BatchFileUploader from './pdf/BatchFileUploader';
import FilePreviewItem from './pdf/FilePreviewItem';
import Instructions from './pdf/Instructions';
import Toast from './Toast';

function PdfToXls() {
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [downloadUrls, setDownloadUrls] = useState([]);
    const [zipDownloadUrl, setZipDownloadUrl] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [processingStatus, setProcessingStatus] = useState({});
    const [overallProgress, setOverallProgress] = useState(0);
    try {

        const handleFileSelect = (selectedFiles) => {
            if (!selectedFiles || selectedFiles.length === 0) return;
            
            setError('');
            setSuccess(false);
            setDownloadUrls([]);
            setZipDownloadUrl(null);

            // Validate all files
            const newFiles = [];
            const errors = [];

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                const validation = validatePdfFile(file);
                
                if (validation.isValid) {
                    // Check for duplicates
                    if (!newFiles.some(f => f.name === file.name && f.size === file.size)) {
                        newFiles.push(file);
                    }
                } else {
                    errors.push(`${file.name}: ${validation.error}`);
                }
            }

            if (errors.length > 0) {
                setError(`部分檔案無法上傳: ${errors.join('; ')}`);
            }

            if (newFiles.length > 0) {
                setFiles(prevFiles => [...prevFiles, ...newFiles]);
            }
        };

        const handleRemoveFile = (index) => {
            setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
            setProcessingStatus(prev => ({
                ...prev,
                [index]: {}
            }));
        };

        const handleRemoveAllFiles = () => {
            setFiles([]);
            setProcessingStatus({});
            setDownloadUrls([]);
            setZipDownloadUrl(null);
            setSuccess(false);
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setSuccess(false);
            setDownloadUrls([]);
            setZipDownloadUrl(null);

            if (files.length === 0) {
                setError('請選擇至少一個檔案');
                return;
            }

            // Check if user has enough credits
            try {
                const userData = getUserData();
                if (!userData || !userData.id) {
                    throw new Error('請先登入');
                }
            } catch (err) {
                setError(err.message);
                return;
            }

            setIsLoading(true);
            setProcessingStatus({});

            try {
                const results = [];
                let completedFiles = 0;
                
                // Initialize processing status for all files
                const initialStatus = {};
                files.forEach((file, index) => {
                    initialStatus[index] = {
                        status: 'pending',
                        progress: 0,
                        message: '等待處理'
                    };
                });
                setProcessingStatus(initialStatus);

                // Process files sequentially to avoid overwhelming the server
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    // Update status to processing
                    setProcessingStatus(prev => ({
                        ...prev,
                        [i]: {
                            status: 'processing',
                            progress: 0,
                            message: '處理中...'
                        }
                    }));
                    
                    // Simulate progress updates
                    const progressInterval = setInterval(() => {
                        setProcessingStatus(prev => {
                            const fileStatus = prev[i] || {};
                            if (fileStatus.status === 'processing' && fileStatus.progress < 90) {
                                return {
                                    ...prev,
                                    [i]: {
                                        ...fileStatus,
                                        progress: Math.min(fileStatus.progress + 10, 90)
                                    }
                                };
                            }
                            return prev;
                        });
                    }, 500);
                    
                    try {
                        // Convert the PDF to Excel
                        const result = await convertPdfToExcel(file);
                        
                        clearInterval(progressInterval);
                        
                        if (result.success) {
                            // Create a blob and URL for the Excel file
                            const blob = new Blob([result.fileBuffer], { 
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                            });
                            
                            const url = URL.createObjectURL(blob);
                            const fileName = file.name.replace('.pdf', '.xlsx');
                            
                            results.push({
                                originalFile: file,
                                url: url,
                                fileName: fileName,
                                blob: blob
                            });
                            
                            // Update status to completed
                            setProcessingStatus(prev => ({
                                ...prev,
                                [i]: {
                                    status: 'completed',
                                    progress: 100,
                                    message: '完成'
                                }
                            }));
                        } else {
                            throw new Error(result.error || '轉換失敗');
                        }
                    } catch (err) {
                        clearInterval(progressInterval);
                        
                        // Update status to error
                        setProcessingStatus(prev => ({
                            ...prev,
                            [i]: {
                                status: 'error',
                                progress: 0,
                                message: `錯誤: ${err.message}`
                            }
                        }));
                    }
                    
                    completedFiles++;
                    setOverallProgress(Math.round((completedFiles / files.length) * 100));
                }
                
                setDownloadUrls(results);
                
                // Create a zip file if there are multiple results
                if (results.length > 1) {
                    const zip = new JSZip();
                    
                    // Add all Excel files to the zip
                    for (const result of results) {
                        zip.file(result.fileName, result.blob);
                    }
                    
                    // Generate the zip file
                    const zipBlob = await zip.generateAsync({ type: 'blob' });
                    const zipUrl = URL.createObjectURL(zipBlob);
                    setZipDownloadUrl(zipUrl);
                }
                
                setSuccess(true);
            } catch (err) {
                setError('轉換過程中發生錯誤，請稍後再試');
                console.error('Conversion error:', err);
            } finally {
                setIsLoading(false);
                setOverallProgress(0);
            }
        };

        const handleDownload = (url, fileName) => {
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            setToast({
                show: true,
                message: '檔案下載中...',
                type: 'success'
            });
            
            setTimeout(() => {
                setToast({ show: false, message: '', type: 'success' });
            }, 3000);
        };

        const handleZipDownload = () => {
            if (!zipDownloadUrl) return;
            
            const a = document.createElement('a');
            a.href = zipDownloadUrl;
            a.download = 'excel_files.zip';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            setToast({
                show: true,
                message: 'ZIP 檔案下載中...',
                type: 'success'
            });
            
            setTimeout(() => {
                setToast({ show: false, message: '', type: 'success' });
            }, 3000);
        };

        return (
            <div className="container mx-auto px-4 py-12" data-name="pdf-to-xls-page">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-name="converter-container">
                        <div className="px-8 py-6 bg-gradient-to-r from-green-600 to-green-400">
                            <h1 className="text-2xl font-bold text-white" data-name="page-title">
                                PDF 收據轉 Excel
                            </h1>
                            <p className="text-white/80 mt-2">
                                將 PDF 收據檔案批量轉換成 Excel 表格
                            </p>
                        </div>

                        <div className="p-8">
                            <form onSubmit={handleSubmit} className="space-y-6" data-name="converter-form">
                                <BatchFileUploader 
                                    onFilesSelect={handleFileSelect}
                                    isLoading={isLoading}
                                />

                                {files.length > 0 && (
                                    <div className="space-y-4" data-name="selected-files">
                                        <div className="flex justify-between items-center">
                                            <h3 className="font-medium text-lg">已選擇的檔案 ({files.length})</h3>
                                            <button
                                                type="button"
                                                onClick={handleRemoveAllFiles}
                                                className="text-red-500 hover:text-red-700 text-sm"
                                                disabled={isLoading}
                                                data-name="remove-all-button"
                                            >
                                                <i className="fas fa-trash-alt mr-1"></i>
                                                清除所有
                                            </button>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                                            {files.map((file, index) => (
                                                <FilePreviewItem 
                                                    key={`${file.name}-${index}`}
                                                    file={file}
                                                    onRemove={() => handleRemoveFile(index)}
                                                    isLoading={isLoading}
                                                    status={processingStatus[index] || {}}
                                                />
                                            ))}
                                        </div>
                                        <p className="text-sm text-gray-500">
                                            注意：每個檔案將扣除 1 點數，共需 {files.length} 點數
                                        </p>
                                    </div>
                                )}

                                {error && (
                                    <div 
                                        className="bg-red-50 border-l-4 border-red-400 p-4 rounded"
                                        role="alert"
                                        data-name="error-message"
                                    >
                                        <div className="flex">
                                            <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                            <p className="text-red-700">{error}</p>
                                        </div>
                                    </div>
                                )}

                                {isLoading && (
                                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded" data-name="loading-message">
                                        <div className="flex flex-col">
                                            <div className="flex items-center">
                                                <i className="fas fa-spinner fa-spin text-blue-400 mr-3"></i>
                                                <p className="text-blue-700">正在處理檔案... ({Math.round(overallProgress)}%)</p>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                                <div 
                                                    className="bg-blue-600 h-2.5 rounded-full" 
                                                    style={{ width: `${overallProgress}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {success && downloadUrls.length > 0 && (
                                    <div 
                                        className="bg-green-50 border-l-4 border-green-400 p-4 rounded"
                                        role="alert"
                                        data-name="success-message"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center">
                                                <i className="fas fa-check-circle text-green-400 mr-3"></i>
                                                <p className="text-green-700">檔案轉換成功！</p>
                                            </div>
                                            {zipDownloadUrl && (
                                                <button
                                                    type="button"
                                                    onClick={handleZipDownload}
                                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center"
                                                    data-name="zip-download-button"
                                                >
                                                    <i className="fas fa-file-archive mr-2"></i>
                                                    下載全部 (ZIP)
                                                </button>
                                            )}
                                        </div>
                                        
                                        <div className="mt-4 space-y-2">
                                            {downloadUrls.map((item, index) => (
                                                <div 
                                                    key={index}
                                                    className="flex justify-between items-center bg-white p-2 rounded border border-green-200"
                                                    data-name={`download-item-${index}`}
                                                >
                                                    <div className="flex items-center">
                                                        <i className="fas fa-file-excel text-green-500 mr-2"></i>
                                                        <span className="text-sm">{item.fileName}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownload(item.url, item.fileName)}
                                                        className="text-sm px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                                        data-name={`download-button-${index}`}
                                                    >
                                                        <i className="fas fa-download mr-1"></i>
                                                        下載
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all flex items-center justify-center"
                                    disabled={files.length === 0 || isLoading}
                                    data-name="submit-button"
                                >
                                    {isLoading ? (
                                        <Fragment>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                            轉換中...
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <i className="fas fa-sync mr-2"></i>
                                            開始轉換 ({files.length} 檔案)
                                        </Fragment>
                                    )}
                                </button>
                            </form>

                            <Instructions />
                        </div>
                    </div>
                </div>
                
                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => setToast({ ...toast, show: false })} 
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('PdfToXls page error:', error);
        reportError(error);
        return null;
    }
}

export default PdfToXls;